/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */

import React, { useEffect, useState } from "react";
import cn from "classnames";
import sass from "../../index.module.sass";
import { ReactComponent as RemoveRow } from "images/TrashCan.svg";
import ModalPortal from "components-new/ModalPortal";
import AcceptRemove from "../../AcceptRemove";
import { useModalStepTwoDotTwo } from "../../index";
import { Button } from "components/shared";
import TextArea from "components-new/Textarea";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "components/shared/Button/Button";
import InputTemp from "components-new/InputTemp";
import { $api } from "libs/axios";
import useDebounce from "hooks/useDebounce";
import Snackbar from "components-new/Snackbar";

/**
 * Интерфейс для опций select'а
 */
interface IOptions {
	id: number;
	address: string;
}

/**
 * Интерфейс входящих данных от родителя
 */
export interface ILocation {
	id: number;
	name: string | null;
	address: {
		address: string;
		id: number | null;
	};
}

/**
 * Описание пропсов от родителя
 * @property data - данные для таблицы
 * @property nameOrganization - имя организации
 * @property deleteRow - функция для изменения стэйта в родителе
 */
interface IProps {
	data: ILocation[];
	nameOrganization?: string;
	deleteRow(row: ILocation[]): void;
	tab: number;
	activeTab: number;
	close: boolean;
	direction: string;
}

/**
 * функция поиска адреса на сервере, если запрос прошел нормально, возвращает данные, если нет false
 */
async function requestAddress(search: string) {
	try {
		const request = await $api.get(`/directory/construction-objects/search?query=${search}`);
		return request.data;
	} catch (e) {
		return false;
	}
}

const LocationVols: React.FC<IProps> = ({ data = [], deleteRow, activeTab, tab, close, direction }) => {
	/**
	 * инициальное состояние для модалки редактирования
	 * @property show - показывает или нет модалку
	 * @property data - данные для редактирования
	 */
	const initialModalEdit = {
		show: false,
		data: {} as ILocation,
	};

	/**
	 * стэйт для редактирования строки
	 * @property show - для модалки
	 * @property data - прокидывает данные в модалку
	 */
	const [edit, setEdit] = useState<{ show: boolean; data: ILocation }>(initialModalEdit);

	/**
	 * включение режима редактирования
	 * установка данных строки для редактирования
	 */
	function selectRow(row: ILocation) {
		setEdit({
			show: true,
			data: row,
		});
	}

	/**
	 * выключение режима редактирования
	 */
	function closeEditModal() {
		setEdit(initialModalEdit);
	}

	/**
	 * прослушиватель события ввода пользователем
	 */
	function onChange(e: React.ChangeEvent<HTMLTextAreaElement | HTMLSelectElement>) {
		const name = e.target.name as keyof ILocation;
		const value = e.target.value as ILocation[keyof ILocation];
		if (name !== "address") {
			setEdit((prevState) => ({
				...prevState,
				data: {
					...prevState.data,
					[name]: value,
				},
			}));
		}
	}

	/**
	 * применить изменения
	 * ищется индекс элемента в массиве, если найдет, удаляет старый элемент, вставляет новый на его место
	 */
	function applyRow() {
		const { id, name, address } = edit.data;
		let newData: any;
		$api.put(`/object/location/${id}`, { address: address.id, name }).then((response) => {
			const result = response.data;

			newData = [...data].map((_) => (_.id === result.id ? { ...result } : { ..._ }));
			deleteRow(newData);
			closeEditModal();
			Snackbar({ factor: "light", type: "success", title: "Успешно сохранено", position: "top-center" });
		});
	}

	/**
	 * модалка подтверждения удаления строки таблицы
	 */
	const { abort, submit, modal, openModal } = useModalStepTwoDotTwo<number>(removeItem);

	function removeItem(id: number) {
		$api.delete("/object/location/" + id).then(() => {
			const newData: ILocation[] = [...data].filter((_) => _.id !== id);
			deleteRow(newData);
			Snackbar({ factor: "light", type: "success", title: "Успешно удалено", position: "top-center" });
		});
	}

	/**
	 * стэйт для отображения индикатора загрузки в select'е
	 */
	const [loadAddress, setLoadAddress] = useState(false);

	/**
	 * стэйт ввода пользователем адреса и дальнейшего поиска
	 */
	const [search, setSearch] = useState("");

	/**
	 * стэйт хранения списка адресов полученных с сервера
	 */
	const [address, setAddress] = useState<IOptions[]>([]);

	const debounce = useDebounce(search, 300);
	useEffect(() => {
		setLoadAddress(true);
		requestAddress(debounce as string).then((r) => {
			if (r) {
				setAddress(r);
			}
			setLoadAddress(false);
		});
	}, [debounce]);

	return (
		<>
			{modal.active && (
				<ModalPortal title={"Удалить строку?"} handleClose={abort} outSide={false}>
					<AcceptRemove submit={submit} abort={abort} />
				</ModalPortal>
			)}
			{edit.show && (
				<ModalPortal title={"Редактирование строки"} handleClose={closeEditModal} outSide={false}>
					<div className={sass.edit}>
						<div className={sass.form}>
							<TextArea
								autosize={true}
								placeholder={"Введите наименование сегмента"}
								maxLength={150}
								label={"Наименование сегмента"}
								name={"name"}
								value={(edit.data.name as string) || ""}
								onChange={onChange}
							/>
							<InputTemp
								label={"Адрес размещения объекта"}
								placeholder={"Выберете адрес"}
								load={loadAddress}
								onSelect={(obj) => {
									// setValue(prevState => ({...prevState,current:obj,search:obj.address}))
									setEdit((prevState) => ({ ...prevState, data: { ...prevState.data, address: obj } }));
									setSearch(obj.address);
								}}
								name={"address"}
								value={search}
								onChange={(e) => {
									setSearch(e.target.value);
								}}
								options={address}
								field={"address"}
							/>
						</div>
						<div className={sass.endBtn}>
							<Button variant={BUTTON_VARIANTS.OUTLINED} full={true} onClick={closeEditModal}>
								Отменить
							</Button>
							<Button color={BUTTON_COLORS.PRIMARY} full={true} onClick={applyRow}>
								Сохранить
							</Button>
						</div>
					</div>
				</ModalPortal>
			)}
			<section
				className={cn(
					sass.page__item,
					sass.page__item_active,
					tab === activeTab && !close ? sass[`${direction}_enter`] : sass[`${direction}_out`],
				)}>
				<div className={sass.table}>
					<div className={sass.first}>
						<table cellSpacing="0">
							<thead>
								<tr>
									<th>№</th>
									<th>Наименование сегмента</th>
									<th>Адрес размещения объекта</th>
								</tr>
							</thead>
						</table>
					</div>

					<table data-comment-false={true} cellSpacing="0">
						<tbody>
							{data.map((row, index) => (
								<React.Fragment key={row.id}>
									<tr
										onClick={() => {
											selectRow(row);
											setSearch(row.address.address);
										}}>
										<td>{index + 1}</td>
										<td>{row.name || "—"}</td>
										<td>
											{row.address.address || "—"}
											<RemoveRow
												onClick={(e) => {
													e.stopPropagation();
													openModal(row.id);
												}}
												width={18}
												height={18}
											/>
										</td>
									</tr>
									{/*</Tooltip>*/}
								</React.Fragment>
							))}
						</tbody>
					</table>
				</div>
			</section>
		</>
	);
};

export default LocationVols;
